import { useState } from 'react';
import { motion } from 'framer-motion';
import {
    UserCheck,
    Home,
    Briefcase,
    Car,
    Building2,
    Landmark,
    Store,
    TrendingUp,
    Wallet,
    ArrowLeftRight,
    ArrowUpRight,
    Check,
} from 'lucide-react';
import { LOANS, preselectProduct } from '../data';

const ICONS = { UserCheck, Home, Briefcase, Car, Building2, Landmark, Store, TrendingUp, Wallet, ArrowLeftRight };
const CATEGORIES = ['All', 'Unsecured', 'Secured', 'Transfer'];

export const Loans = () => {
    const [category, setCategory] = useState('All');
    const loans = category === 'All' ? LOANS : LOANS.filter((l) => l.category === category);

    return (
        <section id="loans" data-testid="loans-section" className="py-24 sm:py-32 bg-[#0D111A]">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 32 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                >
                    <p className="font-mono-x text-xs uppercase tracking-[0.3em] text-emerald-400 mb-4">
                        01 — Credit Suite
                    </p>
                    <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight max-w-2xl">
                        Ten instruments.{' '}
                        <span className="text-slate-500">Every ambition funded.</span>
                    </h2>
                    <div className="mt-8 flex flex-wrap gap-2" data-testid="loan-filter-bar">
                        {CATEGORIES.map((c) => (
                            <button
                                key={c}
                                data-testid={`loan-filter-${c.toLowerCase()}`}
                                onClick={() => setCategory(c)}
                                className={`rounded-full px-4 py-1.5 font-mono-x text-[11px] uppercase tracking-[0.15em] border transition-colors duration-300 ${
                                    category === c
                                        ? 'border-emerald-400/60 bg-emerald-500/10 text-emerald-300'
                                        : 'border-white/10 text-slate-400 hover:border-white/25 hover:text-slate-200'
                                }`}
                            >
                                {c}
                            </button>
                        ))}
                    </div>
                </motion.div>

                <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {loans.map((loan, i) => {
                        const Icon = ICONS[loan.icon];
                        return (
                            <motion.article
                                key={loan.id}
                                data-testid={`loan-card-${loan.id}`}
                                initial={{ opacity: 0, y: 44 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: '-60px' }}
                                transition={{ duration: 0.7, delay: (i % 3) * 0.1, ease: [0.16, 1, 0.3, 1] }}
                                whileHover={{ scale: 1.02 }}
                                className="group relative rounded-2xl border border-white/10 bg-[#131924] p-7 flex flex-col hover:border-emerald-400/40 hover:shadow-[0_0_40px_rgba(16,185,129,0.12)] transition-[border-color,box-shadow] duration-300"
                            >
                                <div className="flex items-start justify-between">
                                    <span className="w-11 h-11 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                                        <Icon className="w-5 h-5 text-emerald-400" />
                                    </span>
                                    <div className="text-right">
                                        <span className="block font-mono-x text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                            from <span className="text-emerald-400">{loan.rate}</span> p.a.
                                        </span>
                                        <span className="mt-1 inline-block rounded-full border border-white/10 px-2 py-0.5 font-mono-x text-[9px] uppercase tracking-[0.15em] text-slate-500">
                                            {loan.category}
                                        </span>
                                    </div>
                                </div>
                                <h3 className="mt-5 font-display text-xl sm:text-2xl font-semibold tracking-tight">
                                    {loan.title}
                                </h3>
                                <p className="mt-2 text-sm leading-relaxed text-slate-400">
                                    {loan.tagline}
                                </p>
                                <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
                                    <div>
                                        <div className="text-[10px] font-mono-x uppercase tracking-[0.18em] text-slate-500">
                                            Max amount
                                        </div>
                                        <div className="font-display font-bold mt-0.5">{loan.maxAmount}</div>
                                    </div>
                                    <div>
                                        <div className="text-[10px] font-mono-x uppercase tracking-[0.18em] text-slate-500">
                                            Tenure
                                        </div>
                                        <div className="font-display font-bold mt-0.5">{loan.tenure}</div>
                                    </div>
                                </div>
                                <ul className="mt-5 space-y-2">
                                    {loan.features.map((f) => (
                                        <li key={f} className="flex items-center gap-2 text-xs text-slate-300">
                                            <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                                            {f}
                                        </li>
                                    ))}
                                </ul>
                                <a
                                    href="#apply"
                                    onClick={() => preselectProduct(loan.value)}
                                    data-testid={`loan-apply-${loan.id}`}
                                    className="mt-6 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-400 group-hover:gap-2.5 transition-all duration-300"
                                >
                                    Apply for {loan.title}
                                    <ArrowUpRight className="w-3.5 h-3.5" />
                                </a>
                            </motion.article>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};
